
import React, { useState } from "react";
import { usePlanSelectDialog } from "@/contexts/PlanSelectDialogContext";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/custom-dialog";
import { SubscriptionPlanCard } from "@/components/subscriptions/SubscriptionPlanCard";
import { addMonths } from "date-fns";
import { toast } from "sonner";
import { SubscriptionPlan } from "@/lib/types";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export function PlanSelectDialog() {
  const { open, setOpen } = usePlanSelectDialog();

  const { 
    useCurrentUserSubscriptionQuery, 
    useActiveSubscriptionPlansQuery,
    useCreateUserSubscriptionMutation
  } = useSubscriptions();

  const { 
    data: subscription, 
    isLoading: subscriptionLoading, 
  } = useCurrentUserSubscriptionQuery();

  const { 
    data: plans = [],
    isLoading: plansLoading,
  } = useActiveSubscriptionPlansQuery();

  const createUserSubscription = useCreateUserSubscriptionMutation();
  const [isUpdating, setIsUpdating] = useState(false);
  
  // State for confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    if (subscription?.plan?.id === plan.id) {
      toast.info("You are already subscribed to this plan");
      return;
    }
    
    setSelectedPlan(plan);
    setConfirmDialogOpen(true);
  };

  const handleConfirmPlanChange = async () => {
    if (!selectedPlan) return;
    
    try {
      setIsUpdating(true);
      const endDate = addMonths(new Date(), 1);
      
      await createUserSubscription.mutateAsync({
        plan_id: selectedPlan.id,
        current_period_end: endDate.toISOString()
      });
      
      toast.success("Subscription updated successfully!");
      setOpen(false);
      setConfirmDialogOpen(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error("Failed to update subscription:", error);
      
      // Show a more helpful error message
      if (error.message?.includes('row-level security policy')) {
        toast.error("You don't have permission to update your subscription. Please contact support.");
      } else if (error.message?.includes('create_user_subscription')) {
        toast.error("The subscription system is currently unavailable. Please try again later or contact support.");
      } else {
        toast.error("Failed to update subscription. Please try again.");
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelPlanChange = () => {
    setConfirmDialogOpen(false);
    setSelectedPlan(null);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[1200px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">Choose Your Plan</DialogTitle>
            <DialogDescription className="text-center">
              Select a plan that best fits your contract management needs
            </DialogDescription>
          </DialogHeader>
          {plansLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 py-6">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-[520px]" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 py-6">
              {plans.map((plan) => (
                <SubscriptionPlanCard
                  key={plan.id}
                  plan={plan}
                  isCurrentPlan={subscription?.plan?.id === plan.id}
                  onSelect={() => handleSelectPlan(plan)}
                  isLoading={isUpdating && selectedPlan?.id === plan.id}
                />
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Plan Change</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change to the {selectedPlan?.name} plan?
              {subscription?.plan && (
                <span className="block mt-2">
                  You are currently on the {subscription.plan.name} plan.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelPlanChange} disabled={isUpdating}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmPlanChange} 
              disabled={isUpdating}
              className="bg-primary hover:bg-primary/90"
            >
              {isUpdating ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                "Confirm Change"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
